import { CommonModule } from '@angular/common';
import { AgGridModule } from '@ag-grid-community/angular';
import {
  CellClickedEvent,
  ColumnApi,
  GridApi,
  GridOptions,
  ICellRendererParams,
  IServerSideDatasource,
  RowClickedEvent,
} from '@ag-grid-community/core';
import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { take } from 'rxjs';

import { AccessManagementGroupedDesignationRecord } from '@cdw-ae/common-data';
import { MatDialog } from '@cdw-ae/common-frontend-materials';
import { DrawerContent } from '@cdw-ae/common-ui-components-drawer-content';
import { CdwColDef, CdwSearchGridComponent, CdwSearchGridModule } from '@cdw-ae/common-ui-components-search-grid';
import { CdwConfirmationDialogComponent, CdwDialogModule, ConfirmationDialogResult } from '@cdw-ae/common-ui-components-dialog';
import { ErrorSnackbarComponent } from '@cdw-ae/components-error-snackbar';
import { FilterBarField } from '@cdw-ae/filter-bar-interfaces';
import { TailwindColors } from '../../../../../../common/constants/tailwind-colors';
import { DesignationsFilterSettings } from '../../../../../../common/services/route-config/configs/filter-bar-config.config';

import { DesignationsDataService } from './designations-data.service';
import { userCollectionComparator, rolesComparator, orgUnitComparator } from './designations.utils';

import { AccessManagementFrontendAuthService } from '../access-management-frontend-auth.service';
import { OrgUnitCollectionsDataService } from '../ou-collections/ou-collections-data.service';
import { RolesDataService } from '../roles/roles-data.service';
import { UserCollectionsDataService } from '../user-collections/user-collections-data.service';
import { EmptyStateOverlayComponent } from '../../../../../../components/empty-state-overlay/empty-state-overlay.component';

@Component({
  selector: 'designations',
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    CdwSearchGridModule,
    CdwDialogModule,
    EmptyStateOverlayComponent,
  ],
  templateUrl: './designations.component.html',
  styleUrl: './designations.component.scss',
})
export class DesignationsComponent implements OnInit {
  @ViewChild('designationsGrid')
  public designationGrid!: CdwSearchGridComponent<AccessManagementGroupedDesignationRecord>;

  @Output() updateDrawerContent = new EventEmitter<unknown>();

  public selectedDesignation: AccessManagementGroupedDesignationRecord | null = null;

  public isLoading = false;

  public isRefreshing = false;

  public gridApi!: GridApi;

  public columnApi!: ColumnApi;

  public dataSource!: IServerSideDatasource;

  public filterBarSettings: FilterBarField[];

  public selectedFilterBarFields!: string[];

  public itemLabel = 'designation';

  public designationsSearchComponentId = 'designationsComponent';

  public readonly searchFieldMask: string[] = [
    'role.name',
    'userCollection.name',
    'user.name',
    'orgUnit.value',
    'orgUnitCollection.name',
  ];

  public drawerContent: DrawerContent[] = [];

  public drawerContentTitle = '';

  public filterBySelectedColumnName = 'id';

  public groupedData: AccessManagementGroupedDesignationRecord[] = [];

  public gridOptions: GridOptions = {
    rowModelType: 'clientSide',
    cacheBlockSize: 100,
    suppressCopyRowsToClipboard: true,
    pagination: true,
    paginationAutoPageSize: false,
    paginationPageSize: 50,
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
  };

  public columnDefs: CdwColDef[] = [
    {
      field: 'user',
      headerName: 'User/User Collection',
      resizable: true,
      width: 400,
      sort: 'asc',
      comparator: userCollectionComparator,
      cellRenderer: (params: ICellRendererParams) => {
        const user = params.value as { name: string; type: string; id?: number };
        if (!user) return '';
        const isCollection = user.type === 'collection';

        // Apply blue color only to collections, grey for individual users
        const color = isCollection ? this.collectionLinkColor : this.defaultTextColor;
        const className = isCollection ? 'designation-collection-link' : 'designation-individual-item';
        const cursor = isCollection ? 'cursor: pointer;' : '';

        // Return HTML string with data attributes for collections
        if (isCollection) {
          // Even if id is missing, still add cursor pointer for collections
          if (user.id) {
            return `<span class="${className}" data-user-collection-id="${user.id}" style="color: ${color}; ${cursor}">${user.name}</span>`;
          } else {
            // Collection but no ID - still make it look clickable
            return `<span class="${className}" style="color: ${color}; ${cursor}">${user.name}</span>`;
          }
        }

        return `<span class="${className}" style="color: ${color};">${user.name}</span>`;
      },
    },
    {
      field: 'roles',
      headerName: 'Roles',
      resizable: true,
      width: 300,
      comparator: rolesComparator,
      cellRenderer: (params: ICellRendererParams) => {
        const roles = params.value as Array<{ id: number; name: string }>;
        if (!roles || roles.length === 0) return '';

        // Calculate height based on number of items
        const itemCount = roles.length;
        const lineHeight = 28; // pixels per line
        const padding = 20; // top + bottom padding
        const minHeight = Math.max(60, itemCount * lineHeight + padding);

        // Build HTML string for roles
        const rolesList = roles
          .map(
            (r) =>
              `• <span class="designation-role-link" data-role-id="${r.id}" style="color: ${this.collectionLinkColor}; cursor: pointer;">${r.name}</span>`
          )
          .join('<br/>');

        // Set row height dynamically
        if (params.node?.setRowHeight) {
          const currentHeight = params.node.rowHeight || 60;
          const newHeight = Math.max(currentHeight, minHeight);
          params.node.setRowHeight(newHeight);
          params.api.onRowHeightChanged();
        }

        return `<div style="white-space: pre-wrap; line-height: 1.5; padding: 8px 0;">${rolesList}</div>`;
      },
    },
    {
      field: 'orgUnit',
      headerName: 'OU/OU Collections',
      resizable: true,
      width: 400,
      comparator: orgUnitComparator,
      cellRenderer: (params: ICellRendererParams) => {
        const orgUnit = params.value as { name: string; type: string; id?: number };
        if (!orgUnit) return '';
        const isCollection = orgUnit.type === 'collection';

        // Apply blue color to both collections and individual OUs (both clickable)
        const color = this.collectionLinkColor;
        const className = isCollection ? 'designation-collection-link' : 'designation-individual-ou';
        const cursor = 'cursor: pointer;';

        // Return HTML string with data attributes
        if (isCollection) {
          // Collection - use data-ou-collection-id
          if (orgUnit.id) {
            return `<span class="${className}" data-ou-collection-id="${orgUnit.id}" style="color: ${color}; ${cursor}">${orgUnit.name}</span>`;
          } else {
            // Collection but no ID - still make it look clickable
            return `<span class="${className}" style="color: ${color}; ${cursor}">${orgUnit.name}</span>`;
          }
        }

        // Individual OU - use data-ou-value with full path; display middle-truncated for readability
        const displayText = this.truncateOuPath(orgUnit.name);
        return `<span class="${className}" data-ou-value="${orgUnit.name}" title="${orgUnit.name}" style="color: ${color}; ${cursor}">${displayText}</span>`;
      },
    },
    {
      cellRenderer: 'deleteItemIconComponent',
      headerName: 'Delete',
      flex: 1,
    },
  ];

  // Theme colors for search grid
  public customTextColor: string = TailwindColors.gray300;
  public firstColumnTextColor: string = TailwindColors.tertiaryBlueLink;

  // Maintainable color definitions for designations
  public collectionLinkColor: string = TailwindColors.tertiaryBlueLink; // Blue for collections
  public defaultTextColor: string = TailwindColors.gray300; // Grey for individual items

  // Show empty state overlay for testing
  showEmptyState = false;

  // HTML content for empty state overlay
  public designationsEmptyStateHtml = this.getEmptyStateHtml();

  constructor(
    public designationsDataService: DesignationsDataService,
    private dialog: MatDialog,
    private accessManagementAuthService: AccessManagementFrontendAuthService,
    private snackbar: MatSnackBar,
    private rolesDataService: RolesDataService,
    private userCollectionsDataService: UserCollectionsDataService,
    private ouCollectionsDataService: OrgUnitCollectionsDataService,
    private router: Router,
  ) {
    this.filterBarSettings = DesignationsFilterSettings.filter((filterBarField: FilterBarField): boolean =>
      this.searchFieldMask.includes(filterBarField.name)
    );
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    await this.accessManagementAuthService.setUserAuthContext();
  }

  onDataLoaded(): void {
    this.isLoading = false;
  }

  deleteDesignationClicked(event: {
    rowId: number | string;
    gridComponentId?: string;
  }) {
    // Get the full row data from the grid using the rowId
    const gridApi = this.designationGrid.params?.api;
    if (!gridApi) {
      this.snackbar.openFromComponent(ErrorSnackbarComponent, {
        data: 'Grid API is not available.',
      });
      return;
    }

    // Find the row data by ID
    let rowData: AccessManagementGroupedDesignationRecord | undefined;
    gridApi.forEachNode((node) => {
      if (node.data?.id === event.rowId) {
        rowData = node.data as AccessManagementGroupedDesignationRecord;
      }
    });

    if (!rowData?.designationIds || rowData.designationIds.length === 0) {
      this.snackbar.openFromComponent(ErrorSnackbarComponent, {
        data: 'Unable to identify designation to delete.',
      });
      return;
    }

    const designationIds = rowData.designationIds;

    const customerId = this.accessManagementAuthService.authContext?.sqlCustomerId ?? null;
    if (!customerId) {
      this.snackbar.openFromComponent(ErrorSnackbarComponent, {
        data: 'Authentication context is not available.',
      });
      return;
    }

    const deleteDialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      data: {
        title: 'Confirmation',
        message: 'Are you sure you want to delete this designation?',
        type: 'warning',
        layout: 'warning-box',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmButtonColor: 'warn',
        hard: true,
      },
      width: '540px',
      maxWidth: '100%',
      disableClose: true,
      hasBackdrop: true,
    });

    deleteDialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(async (result: ConfirmationDialogResult | undefined) => {
        if (result?.confirmed && designationIds.length > 0) {
          // Delete all designations in the group
          const deletePromises = designationIds.map(async (id: number) => {
            const observable = await this.designationsDataService.deleteDesignation(id);
            return new Promise<void>((resolve, reject) => {
              observable.pipe(take(1)).subscribe({
                next: () => resolve(),
                error: (err) => reject(err)
              });
            });
          });

          try {
            await Promise.all(deletePromises);
            this.snackbar.open(`Successfully deleted ${designationIds.length} designation(s).`, 'OK', { duration: 6000 });
            this.refreshSearchGrid();
          } catch {
            this.snackbar.openFromComponent(ErrorSnackbarComponent, {
              data: 'Failed to delete some designations',
            });
          }
        }
      });
  }

  public onRowClicked(event: RowClickedEvent & { handledByCell?: boolean }): void {
    // Check if this was already handled by cell click
    if (event.handledByCell) {
      return;
    }

    // Check if the click originated from a clickable item (role, collection)
    const mouseEvent = event.event;
    if (mouseEvent?.target) {
      const target = mouseEvent.target as HTMLElement;

      // Check the clicked element and its parents (up to 5 levels)
      let currentElement: HTMLElement | null = target;
      let levels = 0;
      while (currentElement && levels < 5) {
        // Check if this element has data attributes
        const roleId = currentElement.getAttribute('data-role-id');
        const userCollectionId = currentElement.getAttribute('data-user-collection-id');
        const ouCollectionId = currentElement.getAttribute('data-ou-collection-id');
        const ouValue = currentElement.getAttribute('data-ou-value');

        if (roleId) {
          this.openRoleDrawer(parseInt(roleId, 10));
          return;
        } else if (userCollectionId) {
          this.openUserCollectionDrawer(parseInt(userCollectionId, 10));
          return;
        } else if (ouCollectionId) {
          this.openOuCollectionDrawer(parseInt(ouCollectionId, 10));
          return;
        } else if (ouValue) {
          this.openOuDrawer(ouValue);
          return;
        }

        currentElement = currentElement.parentElement;
        levels++;
      }
    }

    // Don't show drawer for normal designation row clicks
    // Only role/user collection/OU collection links should open drawers
  }

  emitUpdateDrawerContent(event: RowClickedEvent & { data?: unknown }) {
    this.updateDrawerContent.emit(event.data);
  }

  refreshSearchGrid() {
    this.designationGrid.refreshGrid();
  }

  public onCellClicked(event: unknown): void {
    const cellEvent = event as CellClickedEvent & { handledByCell?: boolean };
    if (!cellEvent.event?.target) return;

    const target = cellEvent.event.target as HTMLElement;

    // Check if clicked element has data attributes
    const roleId = target.getAttribute('data-role-id');
    const userCollectionId = target.getAttribute('data-user-collection-id');
    const ouCollectionId = target.getAttribute('data-ou-collection-id');
    const ouValue = target.getAttribute('data-ou-value');

    // If we found a special item, handle it AND prevent row click
    if (roleId) {
      cellEvent.event.stopPropagation();
      cellEvent.event.preventDefault();
      // Set a flag on the event to indicate this was handled
      cellEvent.handledByCell = true;
      this.openRoleDrawer(parseInt(roleId, 10));
    } else if (userCollectionId) {
      cellEvent.event.stopPropagation();
      cellEvent.event.preventDefault();
      cellEvent.handledByCell = true;
      this.openUserCollectionDrawer(parseInt(userCollectionId, 10));
    } else if (ouCollectionId) {
      cellEvent.event.stopPropagation();
      cellEvent.event.preventDefault();
      cellEvent.handledByCell = true;
      this.openOuCollectionDrawer(parseInt(ouCollectionId, 10));
    } else if (ouValue) {
      cellEvent.event.stopPropagation();
      cellEvent.event.preventDefault();
      cellEvent.handledByCell = true;
      this.openOuDrawer(ouValue);
    }
  }

  // Open role drawer when clicking on a role
  openRoleDrawer(roleId: number): void {
    this.rolesDataService
      .getRoleById(roleId)
      .pipe(take(1))
      .subscribe(
        (response: {
          success: boolean;
          data?: { role?: { id: number; name: string; description: string; roleType?: unknown } };
        }) => {
          if (response.success && response.data && response.data.role) {
            const { role } = response.data;
            this.updateDrawerContent.emit({
              id: role.id,
              name: role.name,
              description: role.description,
              roleType: role.roleType,
              _itemType: 'role', // Add type indicator
            });
          }
        }
      );
  }

  // Open user collection drawer when clicking on a user collection
  openUserCollectionDrawer(userCollectionId: number): void {
    this.userCollectionsDataService
      .getUserCollectionById(userCollectionId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            const userCollection = response.data;

            this.updateDrawerContent.emit({
              id: userCollection.id,
              name: userCollection.name,
              description: userCollection.description,
              userCollectionUsers: userCollection.userCollectionUsers || [],
              _itemType: 'userCollection', // Add type indicator
            });
          }
        }
      });
  }

  // Open OU collection drawer when clicking on an OU collection
  openOuCollectionDrawer(ouCollectionId: number): void {
    this.ouCollectionsDataService
      .getOrgUnitCollectionById(ouCollectionId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            const orgUnitCollection = response.data;

            this.updateDrawerContent.emit({
              id: orgUnitCollection.id,
              name: orgUnitCollection.name,
              description: orgUnitCollection.description,
              ouCollectionOus: orgUnitCollection.ouCollectionOus || [],
              _itemType: 'ouCollection', // Add type indicator
            });
          }
        }
      });
  }

  private truncateOuPath(path: string): string {
    const parts = path.split('/').filter((p) => p.length > 0);
    if (parts.length <= 2) return path;
    return `/${parts[0]}/.../${parts[parts.length - 1]}`;
  }

  // Open OU drawer when clicking on an individual OU
  openOuDrawer(ouPath: string): void {
    this.updateDrawerContent.emit({
      ouPath,
      _itemType: 'orgUnit', // Add type indicator for individual OU
    });
  }

  onAddNewDesignation(): void {
    this.router.navigate(['administration/access-management/designation/edit/new']);
  }

  getEmptyStateHtml(): string {
    return `No designations have been set up. Start by <a href="/administration/access-management/designation/edit/new" class="empty-state-link">adding a new designation</a>.`;
  }
}
